// components/common/table/ModernDatePicker.tsx
import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import clsx from "clsx";
import ReactDOM from "react-dom";

interface ModernDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  autoFocus?: boolean;
  placeholder?: string;
  showColorLegend?: boolean; // Show the color legend at bottom
  highlightDates?: {
    today?: Date;
    submitted?: Date[];
    pending?: Date[];
  };
}

const ModernDatePicker: React.FC<ModernDatePickerProps> = ({
  value,
  onChange,
  onKeyDown,
  onBlur,
  autoFocus = false,
  placeholder = "Select date",
  showColorLegend = false,
  highlightDates = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    value ? new Date(value) : new Date()
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });
  
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const calendarHeight = 400; // Approximate height
      const calendarWidth = 320;
      
      let top = rect.bottom + window.scrollY + 8;
      let left = rect.left + window.scrollX;

      // Check if calendar would go off-screen
      if (top + calendarHeight > window.innerHeight + window.scrollY) {
        top = rect.top + window.scrollY - calendarHeight - 8;
      }

      if (left + calendarWidth > window.innerWidth) {
        left = window.innerWidth - calendarWidth - 16;
      }

      setPosition({ top, left });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Auto focus
  useEffect(() => {
    if (autoFocus && triggerRef.current) {
      setTimeout(() => {
        triggerRef.current?.focus();
      }, 50);
    }
  }, [autoFocus]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

    const days = [];
    
    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const getDateType = (date: Date) => {
    const { today, submitted = [], pending = [] } = highlightDates;

    if (today && isSameDay(date, today)) return 'today';
    if (submitted.some(d => isSameDay(date, d))) return 'submitted';
    if (pending.some(d => isSameDay(date, d))) return 'pending';
    return 'default';
  };

  const getDayClassName = (day: { date: Date; isCurrentMonth: boolean }) => {
    const isSelected = selectedDate && isSameDay(day.date, selectedDate);
    const dateType = getDateType(day.date);
    
    const baseClasses = "w-9 h-9 rounded-md text-sm font-medium transition-all duration-150 flex items-center justify-center";
    
    if (!day.isCurrentMonth) {
      return `${baseClasses} text-gray-300 cursor-default`;
    }

    switch (dateType) {
      case 'today':
        return `${baseClasses} ${
          isSelected
            ? 'bg-blue-600 text-white ring-2 ring-blue-300'
            : 'bg-blue-500 text-white hover:bg-blue-600'
        }`;
      
      case 'submitted':
        return `${baseClasses} ${
          isSelected
            ? 'bg-teal-600 text-white ring-2 ring-teal-300'
            : 'bg-teal-500 text-white hover:bg-teal-600'
        }`;
      
      case 'pending':
        return `${baseClasses} ${
          isSelected
            ? 'bg-red-600 text-white ring-2 ring-red-300'
            : 'bg-red-500 text-white hover:bg-red-600'
        }`;
      
      default:
        return `${baseClasses} ${
          isSelected
            ? 'bg-blue-600 text-white ring-2 ring-blue-300'
            : 'text-gray-700 hover:bg-gray-100'
        }`;
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const dateString = date.toISOString().split('T')[0];
    onChange(dateString);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
    onKeyDown?.(e);
  };

  const formatDisplayDate = (date: Date | null) => {
    if (!date) return placeholder;
    const day = date.getDate();
    const month = monthNames[date.getMonth()].substring(0, 3);
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const days = getDaysInMonth(currentMonth);

  const calendar = isOpen && (
    <div
      ref={calendarRef}
      className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-[10000]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '320px',
      }}
    >
      <div className="p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <h3 className="text-base font-medium text-gray-700">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          
          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => (
            <button
              key={index}
              type="button"
              onClick={() => day.isCurrentMonth && handleDateSelect(day.date)}
              className={getDayClassName(day)}
              disabled={!day.isCurrentMonth}
            >
              {day.date.getDate()}
            </button>
          ))}
        </div>

        {/* Color Legend */}
        {showColorLegend && (
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-600">Today</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
              <span className="text-xs text-gray-600">Submitted</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-gray-600">Pending</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        className={clsx(
          "w-full border rounded px-3 py-1.5 pr-8 outline-none text-sm bg-white text-left transition-colors",
          isOpen ? "border-blue-600 ring-2 ring-blue-100" : "border-blue-500 hover:border-blue-600"
        )}
        autoFocus={autoFocus}
      >
        {formatDisplayDate(selectedDate)}
      </button>
      <Calendar 
        size={16} 
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" 
      />
      
      {typeof document !== "undefined" && ReactDOM.createPortal(calendar, document.body)}
    </div>
  );
};

export default ModernDatePicker;

